import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

interface RegisterMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: (
    nationalId: string,
    password: string,
    fullName: string,
    role: 'admin' | 'member' | 'clerk',
    phone?: string,
    idPhotoFile?: File,
    isBulkImport?: boolean
  ) => Promise<void>;
  isLoading?: boolean;
}

const RegisterMemberModal = ({
  isOpen,
  onClose,
  onRegister,
  isLoading = false,
}: RegisterMemberModalProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    full_name: string | null;
    national_id: string | null;
    phone: string | null;
    national_id_photo_url: string | null;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isExistingAccount, setIsExistingAccount] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState<{
    id: string;
    full_name: string | null;
    national_id: string | null;
    phone: string | null;
    national_id_photo_url: string | null;
  } | null>(null);
  const [nationalId, setNationalId] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'clerk'>('member');
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const [errors, setErrors] = useState<{
    phone?: string;
    password?: string;
    confirmPassword?: string;
    fullName?: string;
    general?: string;
  }>({});

  if (!isOpen) return null;

  const passwordRequired = role !== 'clerk' && !isExistingAccount;

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!isExistingAccount && role !== 'clerk' && !fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!nationalId.trim()) {
      newErrors.general = 'National ID is required';
    }

    if (
      role === 'member' &&
      !idPhotoFile &&
      (!isExistingAccount || (isExistingAccount && !selectedExisting?.national_id_photo_url))
    ) {
      newErrors.general = 'National ID card photo is required for members';
    }

    if (phone.trim()) {
      // Basic phone validation - should be digits only or with + prefix
      const cleanedPhone = phone.replace(/[^\d+]/g, '');
      if (cleanedPhone.length < 7) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (passwordRequired) {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearchExisting = async () => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setErrors((prev) => ({
        ...prev,
        general: 'Please enter at least 3 characters to search by phone or National ID.',
      }));
      return;
    }

    setIsSearching(true);
    setErrors((prev) => ({ ...prev, general: undefined }));
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, national_id, phone, national_id_photo_url')
        .or(`national_id.ilike.%${q}%,phone.ilike.%${q}%`)
        .limit(8);

      if (error) throw error;
      setSearchResults((data || []) as any);
    } catch (err: any) {
      console.error('Error searching members:', err);
      setSearchResults([]);
      setErrors((prev) => ({
        ...prev,
        general: err.message || 'Failed to search members. Please try again.',
      }));
    } finally {
      setIsSearching(false);
    }
  };

  const applyExistingProfile = (p: {
    id: string;
    full_name: string | null;
    national_id: string | null;
    phone: string | null;
    national_id_photo_url: string | null;
  }) => {
    setSelectedExisting(p);
    setIsExistingAccount(true);
    setNationalId(p.national_id || '');
    setPhone(p.phone || '');
    setFullName(p.full_name || '');
    // Existing account: no password required in this flow.
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setErrors((prev) => ({ ...prev, password: undefined, confirmPassword: undefined, general: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Format phone number (optional)
      let formattedPhone: string | undefined = undefined;
      if (phone.trim()) {
        formattedPhone = phone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          // Default to +678 if no country code
          formattedPhone = '+678' + formattedPhone.replace(/^\+?678/, '');
        }
      }

      await onRegister(
        nationalId.trim(),
        passwordRequired ? password : '',
        fullName.trim(),
        role,
        formattedPhone,
        idPhotoFile || undefined
      );
      
      // Reset form on success
      setSearchQuery('');
      setSearchResults([]);
      setIsExistingAccount(false);
      setSelectedExisting(null);
      setNationalId('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setFullName('');
      setRole('member');
      setIdPhotoFile(null);
      setErrors({});
      setNeedsUpgrade(false);
      onClose();
    } catch (error: any) {
      setNeedsUpgrade(error.needsUpgrade === true);
      setErrors({
        general: error.message || 'Failed to register member. Please try again.',
      });
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsExistingAccount(false);
    setSelectedExisting(null);
    setNationalId('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setRole('member');
    setIdPhotoFile(null);
    setErrors({});
    setNeedsUpgrade(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200">
      <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="UserPlus" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Register New Member</h3>
            <p className="text-sm text-muted-foreground">
              {role === 'clerk'
                ? 'Add an existing member as a clerk (no password needed)'
                : 'Create a new member account with a password'}
            </p>
          </div>
        </div>

        {errors.general && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive font-medium">
                {errors.general}
                {needsUpgrade && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Clear form and close modal first
                        setPhone('');
                        setPassword('');
                        setConfirmPassword('');
                        setFullName('');
                        setErrors({});
                        setNeedsUpgrade(false);
                        onClose();
                        // Small delay to ensure modal is fully unmounted and form is cleared before navigation
                        setTimeout(() => {
                          navigate('/subscription-management');
                        }, 200);
                      }}
                      className="underline hover:no-underline font-semibold"
                    >
                      Upgrade your plan
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off" data-form-type="other" data-lpignore="true" data-1p-ignore="true">
          {/* Hidden dummy fields to confuse password managers */}
          <input type="text" name="fake-username" autoComplete="username" style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} tabIndex={-1} readOnly />
          <input type="password" name="fake-password" autoComplete="current-password" style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} tabIndex={-1} readOnly />

          <div className="p-3 rounded-md border border-border bg-muted/30">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  label="Search existing member (Phone or National ID)"
                  placeholder="Type phone or National ID (min 3 chars)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isLoading || isSearching}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleSearchExisting}
                loading={isSearching}
                disabled={isLoading}
              >
                Search
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-muted-foreground">Select a result to auto-fill and add to this group:</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {searchResults.map((p) => (
                    <div
                      key={p.id}
                      className={`flex items-center justify-between gap-3 p-2 rounded-md border ${
                        selectedExisting?.id === p.id ? 'border-primary bg-primary/5' : 'border-border bg-background'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {p.full_name || 'Unnamed user'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          NID: {p.national_id || '—'} · Phone: {p.phone || '—'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={selectedExisting?.id === p.id ? 'default' : 'outline'}
                        onClick={() => applyExistingProfile(p)}
                        disabled={!p.national_id}
                      >
                        {selectedExisting?.id === p.id ? 'Selected' : 'Use'}
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: results without a National ID can’t be added via this flow.
                </p>
              </div>
            )}
          </div>
          
          <Input
            type="text"
            label="Full Name"
            placeholder="Enter member's full name"
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              if (errors.fullName) setErrors({ ...errors, fullName: undefined });
            }}
            error={errors.fullName}
            required={role !== 'clerk'}
            disabled={isLoading}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
          />

          <Input
            type="text"
            label="National ID (Login ID)"
            placeholder="Enter member's National ID"
            value={nationalId}
            onChange={(e) => {
              setIsExistingAccount(false);
              setSelectedExisting(null);
              setNationalId(e.target.value);
              if (errors.general) setErrors({ ...errors, general: undefined });
            }}
            required
            disabled={isLoading}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
          />

          {role === 'member' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">National ID Card Photo</label>
              <input
                type="file"
                accept="image/*"
                className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                disabled={isLoading}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setIdPhotoFile(file);
                  if (errors.general) setErrors({ ...errors, general: undefined });
                }}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Required for members. Take a clear photo of the National ID card.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => {
                const nextRole = e.target.value as 'admin' | 'member' | 'clerk';
                setRole(nextRole);
                // Clerk is always an existing user in this flow, so clear password fields.
                if (nextRole === 'clerk') {
                  setPassword('');
                  setConfirmPassword('');
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                  setErrors((prev) => ({ ...prev, password: undefined, confirmPassword: undefined }));
                }
              }}
              className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
              disabled={isLoading}
            >
              <option value="member">Member</option>
              <option value="clerk">Clerk</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <Input
              type="tel"
              label="Phone Number (Optional)"
              placeholder="Optional phone number (e.g., 1234567 or +6781234567)"
              value={phone}
              onChange={(e) => {
                setIsExistingAccount(false);
                setSelectedExisting(null);
                setPhone(e.target.value);
                if (errors.phone) setErrors({ ...errors, phone: undefined });
              }}
              error={errors.phone}
              disabled={isLoading}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              National ID will be used for login. Phone is optional for contact.
            </p>
          </div>

          {passwordRequired && (
            <>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: undefined });
                  }}
                  error={errors.password}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  name="member-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-interactive"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                  }}
                  error={errors.confirmPassword}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  name="member-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-interactive"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isLoading}
              iconName="UserPlus"
              iconPosition="right"
            >
              {isExistingAccount ? 'Add to Group' : 'Register Member'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterMemberModal;

